import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Rocket, TrendingDown, Calendar, Coins, Zap, RotateCcw, Trophy, AlertCircle, Repeat } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const STRATEGIES = [
  { code: "proporcional", label: "Proporcional", description: "Reparte el extra entre todas las obligaciones activas según su peso" },
  { code: "avalancha", label: "Avalancha (mayor interés)", description: "Amortiza primero la deuda con mayor interés — máximo ahorro económico" },
  { code: "bola_nieve", label: "Bola de nieve (menor deuda)", description: "Elimina primero la deuda más pequeña — máxima motivación psicológica" },
];

export default function LibertadFinanciera() {
  const [data, setData] = useState(null);
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [form, setForm] = useState({
    extra_amortization_monthly: 0,
    extra_savings_monthly: 0,
    strategy: "avalancha",
  });

  const load = async () => {
    try { const r = await api.get("/financial-freedom"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const runSim = async () => {
    setSimBusy(true);
    try {
      const r = await api.post("/financial-freedom/simulate", {
        extra_amortization_monthly: Number(form.extra_amortization_monthly) || 0,
        extra_savings_monthly: Number(form.extra_savings_monthly) || 0,
        strategy: form.strategy,
        horizon_months: 360,
      });
      setSim(r.data);
    } catch (e) { toast.error(apiError(e)); }
    setSimBusy(false);
  };

  const resetSim = () => { setSim(null); setForm({ extra_amortization_monthly: 0, extra_savings_monthly: 0, strategy: "avalancha" }); };

  const chartData = useMemo(() => {
    if (!data) return [];
    const base = (data.annual_evolution || []).map((p) => ({
      date: p.date, dateLabel: new Date(p.date).getFullYear().toString(),
      baseline: p.total_debt,
    }));
    if (sim) {
      const bySim = new Map((sim.annual_evolution || []).map((p) => [p.date, p.total_debt]));
      base.forEach((p) => { p.simulated = bySim.get(p.date); });
    }
    return base;
  }, [data, sim]);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  if (!data.has_debts) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 space-y-4" data-testid="freedom-empty">
        <Trophy className="mx-auto text-emerald-500" size={48} />
        <h1 className="font-heading text-3xl font-extrabold">Ya eres libre financieramente</h1>
        <p className="text-muted-foreground">No tienes obligaciones de deuda activas. Solo compromisos recurrentes (si los hay).</p>
        {data.subscriptions?.length > 0 && <SubscriptionsBlock subs={data.subscriptions} />}
      </div>
    );
  }

  const showing = sim || {
    baseline: {
      freedom_date: data.freedom_date, months_to_freedom: data.months_to_freedom,
      total_interest: data.total_interest_baseline, obligations_extinguished: data.obligations_timeline.length,
    },
    simulated: null, difference: null,
  };

  return (
    <div className="space-y-8" data-testid="libertad-page">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-3">
            <Rocket size={28} strokeWidth={2.5} /> Libertad financiera
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Cuándo se extingue tu deuda y qué palancas puedes usar para adelantarlo.</p>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Coins} label="Deuda total actual" value={<Money value={data.current_total_debt} className="tabular text-red-600" />} testid="kpi-total-debt" />
        <Kpi icon={Calendar} label="Fecha libertad estimada" value={data.freedom_date ? fmtDate(data.freedom_date) : "—"} testid="kpi-freedom-date" accent />
        <Kpi icon={TrendingDown} label="Meses hasta libertad" value={data.months_to_freedom ?? "—"} testid="kpi-months" />
        <Kpi icon={Zap} label="Cuotas al mes" value={<Money value={data.current_monthly_debt_payment} className="tabular" />} testid="kpi-monthly" />
      </div>

      {/* Gráfica evolución */}
      <Card className="p-5" data-testid="freedom-chart">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading text-lg font-bold">Evolución anual de la deuda</div>
            <div className="text-xs text-muted-foreground">Curva de amortización hasta 0€ {sim && "· comparativa baseline vs simulación"}</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="baselineGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="simGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => Number(v).toLocaleString("es-ES", { style: "currency", currency: "EUR" })} />
              <Area type="monotone" dataKey="baseline" stroke="#ef4444" strokeWidth={2} fill="url(#baselineGrad)" name="Baseline" />
              {sim && <Area type="monotone" dataKey="simulated" stroke="#10b981" strokeWidth={2} fill="url(#simGrad)" name="Con simulación" />}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Timeline de extinciones */}
      <Card className="p-5" data-testid="freedom-timeline">
        <div className="font-heading text-lg font-bold mb-3">Cuándo se extingue cada obligación</div>
        <div className="space-y-3">
          {data.obligations_timeline.map((t) => {
            const pct = t.months_remaining && data.months_to_freedom
              ? Math.max(5, Math.round((t.months_remaining / data.months_to_freedom) * 100))
              : 100;
            return (
              <div key={t.id} className="border border-border rounded-lg p-3" data-testid={`timeline-${t.id}`}>
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="font-semibold">{t.name} <Badge variant="secondary" className="text-[10px] ml-1">{t.obligation_type}</Badge></div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Fin: </span>
                    <span className="font-semibold">{t.extinction_date ? fmtDate(t.extinction_date) : "—"}</span>
                    <span className="text-muted-foreground text-xs ml-2">({t.months_remaining} meses)</span>
                  </div>
                </div>
                <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-red-500 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Capital inicial: <Money value={t.principal_start} className="tabular" /></span>
                  <span>Intereses hasta el fin: <Money value={t.interest_to_pay} className="tabular" /></span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Simulador */}
      <Card className="p-5" data-testid="freedom-simulator">
        <div className="flex items-center gap-2 mb-2">
          <Zap size={18} />
          <div className="font-heading text-lg font-bold">Simulador de amortización y ahorro extra</div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Añade euros extra al mes y descubre cuánto adelantas tu libertad financiera.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-muted-foreground">Amortización extra mensual (€)</label>
            <Input type="number" step="10" min="0" value={form.extra_amortization_monthly}
              onChange={(e) => setForm({ ...form, extra_amortization_monthly: e.target.value })}
              data-testid="sim-extra-amort" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Ahorro extra mensual (€)</label>
            <Input type="number" step="10" min="0" value={form.extra_savings_monthly}
              onChange={(e) => setForm({ ...form, extra_savings_monthly: e.target.value })}
              data-testid="sim-extra-savings" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Estrategia</label>
            <Select value={form.strategy} onValueChange={(v) => setForm({ ...form, strategy: v })}>
              <SelectTrigger data-testid="sim-strategy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STRATEGIES.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 italic">{STRATEGIES.find((s) => s.code === form.strategy)?.description}</p>

        <div className="flex gap-2 mt-4">
          <Button onClick={runSim} disabled={simBusy} data-testid="sim-run"><Zap size={14} className="mr-1" /> Calcular</Button>
          {sim && <Button variant="outline" onClick={resetSim} data-testid="sim-reset"><RotateCcw size={14} className="mr-1" /> Restablecer</Button>}
        </div>

        {sim && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border p-4" data-testid="sim-result-baseline">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Escenario actual</div>
              <div className="space-y-1 text-sm">
                <Row label="Fecha libertad">{sim.baseline.freedom_date ? fmtDate(sim.baseline.freedom_date) : "—"}</Row>
                <Row label="Meses">{sim.baseline.months_to_freedom ?? "—"}</Row>
                <Row label="Intereses totales"><Money value={sim.baseline.total_interest} className="tabular text-red-600" /></Row>
                <Row label="Obligaciones extinguidas">{sim.baseline.obligations_extinguished}</Row>
              </div>
            </div>
            <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="sim-result-simulated">
              <div className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-bold mb-2">Con simulación</div>
              <div className="space-y-1 text-sm">
                <Row label="Nueva fecha libertad" bold>{sim.simulated.freedom_date ? fmtDate(sim.simulated.freedom_date) : "—"}</Row>
                <Row label="Meses">{sim.simulated.months_to_freedom ?? "—"}</Row>
                <Row label="Intereses totales"><Money value={sim.simulated.total_interest} className="tabular" /></Row>
                <Row label="Obligaciones extinguidas">{sim.simulated.obligations_extinguished}</Row>
              </div>
            </div>
            <div className="md:col-span-2 rounded-xl bg-primary text-primary-foreground p-4 flex flex-wrap items-center justify-around gap-4" data-testid="sim-result-diff">
              <DiffStat label="Meses adelantados" value={sim.difference.months_saved > 0 ? `-${sim.difference.months_saved} meses` : "0"} highlight={sim.difference.months_saved > 0} />
              <DiffStat label="Intereses ahorrados" value={<Money value={sim.difference.interest_saved} className="tabular" />} highlight={sim.difference.interest_saved > 0} />
              <DiffStat label="Obligaciones eliminadas antes" value={sim.difference.obligations_extinguished_extra || 0} highlight={sim.difference.obligations_extinguished_extra > 0} />
            </div>
          </div>
        )}
      </Card>

      {/* Compromisos recurrentes (Grover…) */}
      {data.subscriptions?.length > 0 && <SubscriptionsBlock subs={data.subscriptions} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid, accent }) {
  return (
    <Card className={`p-4 ${accent ? "border-2 border-primary/40 bg-primary/5" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
        <Icon size={13} /> {label}
      </div>
      <div className="mt-1 font-heading text-2xl font-extrabold">{value}</div>
    </Card>
  );
}

function Row({ label, children, bold }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? "font-heading font-bold" : ""}>{children}</span>
    </div>
  );
}

function DiffStat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-widest font-bold text-white/60">{label}</div>
      <div className={`text-xl font-heading font-extrabold ${highlight ? "text-white" : "text-white/50"}`}>{value}</div>
    </div>
  );
}

function SubscriptionsBlock({ subs }) {
  const annual = subs.reduce((s, x) => s + (x.annual_cost_estimate || 0), 0);
  return (
    <Card className="p-5" data-testid="freedom-subscriptions">
      <div className="flex items-center gap-2 mb-3">
        <Repeat size={18} className="text-muted-foreground" />
        <div className="font-heading text-lg font-bold">Compromisos recurrentes</div>
        <Badge variant="secondary" className="text-xs">No forman parte del cálculo de libertad</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Suscripciones tipo Grover, alquiler de bienes y suscripciones financieras. Se muestran aquí como compromisos de tesorería futuros pero no se suman a la deuda a extinguir.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {subs.map((s) => (
          <div key={s.id} className="rounded-lg border border-border p-3" data-testid={`subscription-${s.id}`}>
            <div className="flex justify-between">
              <div className="font-semibold">{s.name}</div>
              <Badge variant="outline" className="text-[10px]">{s.obligation_type}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1 grid grid-cols-2 gap-1">
              <div>Cuota mensual: <Money value={s.monthly_payment} className="tabular" /></div>
              <div>Coste anual: <Money value={s.annual_cost_estimate} className="tabular" /></div>
              <div>Fin: {s.end_date ? fmtDate(s.end_date) : "indef."}</div>
              <div>Ya pagado: <Money value={s.total_paid} className="tabular" /></div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-md bg-secondary/40 p-3 text-sm flex items-center justify-between">
        <span className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Coste anual total</span>
        <Money value={annual} className="tabular font-heading text-xl font-extrabold" />
      </div>
    </Card>
  );
}
